export type YId = string & { __type: 'YID' };
export type YTaggedId<T extends string> = YId & { __id_type: T };

export type YObjectId = string & { __type: 'YObjectId' };
export type YMaybeObjectId = YObjectId | '' | { __type: 'YMaybeObjectId' };

export type YTimeString = string & { __type: 'YTimeString' };
export type YDateString = string & { __type: 'YDateString' };
export type YInstallId = string & { __type: 'YInstallId' };
export type YMonthSpec = string & { __type: 'YMonthSpec' };
export type YCountryId = 'DE' | 'SE';

export type YFlags<T extends string> = { [k in T]?: true };
export type YFlagsUpdate<T extends string> = { [k in T]?: boolean };

export interface YDeferred<T> {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: any) => void;
}

export type YPaymentProvider = 'trustly' | 'stripe';
export type YSerializable = string | number | boolean | null | undefined | YSerializable[] | { [key: string]: YSerializable };

export namespace $id {
    export const nil_object_id = '000000000000000000000000' as YMaybeObjectId;

    export function isObjectId(o: string | undefined): o is YObjectId {
        return o?.length == 24 && o != nil_object_id;
    }

    export function isValidObjectId(o: YMaybeObjectId | undefined): o is YObjectId {
        return !!o && o != nil_object_id;
    }

    export function maybeObjectId(o: YMaybeObjectId | undefined) {
        return isValidObjectId(o) ? o : undefined;
    }

    const nil_id = '00000000-0000-0000-0000-000000000000';

    export function isValidId(o: string | undefined): o is YId {
        return !!o && o != nil_id && o.length == 36;
    }

    export function idToObjectId(id: YId): YObjectId {
        const milli = BigInt('0x' + id.slice(0, 8) + id.slice(9, 13));
        const secs = milli / 1000n;

        return (secs.toString(16).padStart(8, '0') + id.slice(16, 18) + id.slice(21, 23) + id.slice(24, 36)) as YObjectId;
    }

    export function maybeObjectIdToId(oid: YMaybeObjectId | undefined): YId | undefined {
        if(isValidObjectId(oid))
            return objectIdToId(oid);
        return undefined;
    }

    export function maybeIdToObjectId(id: YId | undefined): YMaybeObjectId {
        if(id)
            return idToObjectId(id)
        return nil_object_id;
    }

    export function objectIdToId(object_id: YObjectId): YId {
        const secs = BigInt('0x' + object_id.slice(0, 8));
        const milli = (secs * 1000n).toString(16).padStart(12, '0');

        return (milli.slice(0, 8) + '-' + milli.slice(8, 12) + '-70' + object_id.slice(8, 10) + '-80' + object_id.slice(10, 12) + '-' + object_id.slice(12, 24)) as YId;
    }
}
